import { ReactElement, FormEvent, useState, ButtonHTMLAttributes } from "react";
import FrameButton from "../buttons/FrameButton";
import { SocialProvider } from "./SocialLoginButtons";

interface EmailCollectionProps {
    provider: SocialProvider;
    onSubmit: (email: string) => void;
    onBack?: () => void;
    isLoading?: boolean;
}

function EmailCollection({ provider, onSubmit, onBack, isLoading }: EmailCollectionProps): ReactElement {
    const [email, setEmail] = useState("");

    const providerNames = {
        [SocialProvider.GOOGLE]: "Google",
        [SocialProvider.DISCORD]: "Discord",
        [SocialProvider.X]: "X",
        [SocialProvider.TWITCH]: "Twitch",
        [SocialProvider.STEAM]: "Steam",
        [SocialProvider.PHONE]: "phone number",
        [SocialProvider.EMAIL]: "email",
    };

    function handleSubmit(e: FormEvent<HTMLFormElement>): void {
        e.preventDefault();
        if (email) {
            onSubmit(email);
        }
    }

    return (
        <form onSubmit={handleSubmit} className="email-collection">
            <div className="email-collection__header">
                <h4 className="email-collection__title">
                    {provider === SocialProvider.PHONE ? "Verify Your Account" : `✅ ${providerNames[provider]} Connected!`}
                </h4>
                <p className="email-collection__subtitle">
                    {provider === SocialProvider.PHONE
                        ? <>For security, please verify your email address. This is a one-time verification to link your phone and email together.</>
                        : <>Just need your email to finish setup.</>
                    }
                </p>
            </div>

            <div className="login-page_content_email-container">
                <input
                    type="email"
                    required
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    autoFocus
                />
                <p className="email-collection__why">
                    Why? For rewards, updates & account recovery.
                </p>
            </div>

            <FrameButton<ButtonHTMLAttributes<HTMLButtonElement>>
                color="purple"
                size="normal"
                className="login-page_content_login-button"
                forwardProps={{
                    type: "submit",
                    disabled: !email || isLoading,
                }}
            >
                {isLoading ? "Processing..." : "Continue"}
            </FrameButton>

            <button
                type="button"
                className="email-collection__back"
                onClick={() => onBack ? onBack() : window.location.reload()}
                disabled={isLoading}
            >
                Back
            </button>

            <style jsx>{`
                .email-collection {
                    width: 100%;
                }

                .email-collection__header {
                    text-align: center;
                    margin-bottom: 1.5rem;
                }

                .email-collection__title {
                    margin: 0 0 0.5rem 0;
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #4ade80;
                }

                .email-collection__subtitle {
                    margin: 0 0 1.5rem 0;
                    color: #bbb;
                    font-size: 0.95rem;
                }

                .email-collection__why {
                    margin-top: 0.75rem;
                    font-size: 0.8rem;
                    color: #999;
                    line-height: 1.4;
                }
                
                .email-collection__why strong {
                    color: #bbb;
                }

                .email-collection__back {
                    display: block;
                    width: 100%;
                    margin-top: 1rem;
                    padding: 0.75rem;
                    background: transparent;
                    border: none;
                    color: #999;
                    font-size: 0.875rem;
                    cursor: pointer;
                    text-decoration: underline;
                    transition: color 0.2s ease;
                }

                .email-collection__back:hover:not(:disabled) {
                    color: #fff;
                }

                .email-collection__back:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
            `}</style>
        </form>
    );
}

export default EmailCollection;